import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  Form,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Card,
  Spinner,
} from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import logo from '../assets/LogoNavbar.png';
import logoDark from '../assets/LogoHorizontal.png';
import logoWhite from '../assets/LogoHorizontalWhite.png';
import PlanSelect from '../components/PlanSelect';
import type { BillingInterval, Plan } from '../types/types';
import { tracking } from '../util/tracking';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { captureSupabaseError } from '../lib/sentry';

const disableRegister = false;

const freePlanId = '82a8b5f1-1959-4fc3-a37a-f6b8f16d2423';

type RegisterStep = 'landing' | 'plans' | 'form';

export default function Register() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPlanParam = searchParams.get('selectedPlan');
  const sourceParam = searchParams.get('source');
  const isSupplyChainMapSource = sourceParam === 'supply-chain-map';
  const isDPPSource = sourceParam === 'digital-product-passport';
  const hasFreePlanPreselection = selectedPlanParam === 'free';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTermsAndPrivacy, setAcceptedTermsAndPrivacy] = useState(false);
  const [acceptedDpa, setAcceptedDpa] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(
    hasFreePlanPreselection ? freePlanId : null,
  );
  const [registerStep, setRegisterStep] = useState<RegisterStep>(
    hasFreePlanPreselection
      ? 'form'
      : isSupplyChainMapSource || isDPPSource
        ? 'plans'
        : 'landing',
  );
  const [selectedBillingInterval, setSelectedBillingInterval] =
    useState<BillingInterval>('yearly');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  useEffect(() => {
    if (hasFreePlanPreselection) {
      setSelectedPlanId(freePlanId);
      setSelectedBillingInterval('yearly');
      setRegisterStep('form');
      return;
    }

    if (isSupplyChainMapSource) {
      setRegisterStep('plans');
    }

    if (selectedPlanParam) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('selectedPlan');
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    hasFreePlanPreselection,
    isSupplyChainMapSource,
    searchParams,
    selectedPlanParam,
    setSearchParams,
  ]);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      const { data } = await supabase
        .from('plans')
        .select('*')
        .order('tier', { ascending: true });

      setPlans(data ?? []);
      setLoadingPlans(false);
    };

    fetchPlans();
  }, []);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    tracking.log('UI', 'register_submit', {
      page: 'register',
      planId: selectedPlanId,
      billingInterval: selectedBillingInterval,
    });

    if (disableRegister) {
      setError('Registration is currently disabled. Please come back later');
      return;
    }

    setError('');
    setSuccess('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!acceptedTermsAndPrivacy || !acceptedDpa) {
      setError('Please accept all required agreements to continue');
      return;
    }

    setLoading(true);

    try {
      const res = await supabase.functions.invoke('signup-user', {
        body: {
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          plan_id: selectedPlanId,
        },
      });

      if (res.error) {
        tracking.log('ERROR', 'Signup Error', { error: res.error.message });

        let message = 'Something went wrong';

        if (res.error instanceof FunctionsHttpError) {
          try {
            const errorBody = await res.error.context.json();
            message = errorBody.error || errorBody.message || message;
          } catch (e) {
            // fallback if body isn't JSON
            console.error('Error parsing error response:', e);
            message = res.error.message;
          }
        } else {
          message = res.error.message;
        }

        setError(message);
        return;
      }

      const { user_id, organisation_id } = res.data; // from your function response
      if (!user_id) throw new Error('No user_id returned');

      // Now trigger checkout with user email + ID
      const selectedPlan = plans.find((p) => p.id === selectedPlanId);

      if (res.data?.plan_id === freePlanId) {
        setSuccess('Registration successful! Check your email to confirm.');
        // Optionally auto-redirect to login after delay
      } else {
        const stripeLookupKey =
          selectedBillingInterval === 'yearly'
            ? selectedPlan?.stripe_lookup_key_yearly
            : selectedPlan?.stripe_lookup_key;

        if (!stripeLookupKey) {
          setError('Plan not configured');
          return;
        }
        const { data, error } = await supabase.functions.invoke(
          'create-checkout-session',
          {
            body: {
              stripe_lookup_key: stripeLookupKey,
              email: email, // pre-fill
              user_id: user_id, // for client_reference_id
              organisation_id: organisation_id,
              flow: 'signup',
              plan_id: selectedPlanId,
            },
          },
        );

        if (error) throw error;

        const { url } = data;

        if (url) {
          window.location.href = url;
        } else {
          setSuccess('Account created! Redirecting to payment...');
          // fallback redirect or show message
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'auth.register' });
      setError(err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  const selectedPlanName =
    selectedPlan?.name ??
    (selectedPlanId === freePlanId ? 'Free' : 'Unknown Plan');

  const handleChangePlan = () => {
    setSelectedPlanId(null);
    setRegisterStep('plans');

    if (hasFreePlanPreselection) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('selectedPlan');
      setSearchParams(nextSearchParams, { replace: true });
    }
  };

  const handleLandingBadgeClick = (reportType: string) => {
    tracking.log('UI', 'Register landing badge click ', {
      page: 'register',
      reportType,
    });
  };

  const handleLandingPlansClick = () => {
    tracking.log('UI', 'Register landing button click ', {
      page: 'register',
      cta: 'view_plans_get_started',
    });
    setRegisterStep('plans');
  };

  return (
    <div className="bg-light min-vh-100 d-flex align-items-center">
      <Container className="py-5">
        {registerStep === 'landing' ? (
          <div
            className="register-hero rounded-4 overflow-hidden shadow-lg position-relative"
            style={{
              minHeight: '640px',
              backgroundColor: '#0f172a',
            }}
          >
            <Row className="g-0 h-100">
              <Col md={6} className="d-flex flex-column p-4 p-lg-5 text-white">
                <img
                  src={logoWhite}
                  alt="SupplyPassport Logo"
                  className="mb-4 callout-logo"
                />
                <h3>Your Global Compliance, Simplified.</h3>
                <div className="mb-1 text-white">
                  <ul className="fw-bold mb-1 d-none d-md-block">
                    <li>
                      <strong>Digital Product Passport:</strong> Share your
                      product's compliance data with clients and regulators via
                      a secure, verifiable link.
                    </li>
                    <li>
                      Verified Visibility with SupplyPassport compliance profile{' '}
                      <a
                        href="https://supplypassport.app/supplier/dc632034-a493-4b86-9386-4c2c62e70134"
                        target="_blank"
                        rel="noopener noreferrer"
                        umami-track="Register landing profile click"
                        className="text-white"
                      >
                        Example
                      </a>
                    </li>
                    {/* <li>
                      Complete industry-standard questionnaires once and share
                      secure reports with all your clients including ESG, LkSG
                      (Germany), CSDDD, FDV (France), CSRD, and the 2026 ESRS
                      (VSME).
                    </li> */}
                    <li>
                      <strong>AI-Powered Compliance Plan:</strong> Let our AI
                      analyze your data to generate a roadmap for closing
                      compliance gaps automatically.
                    </li>
                    <li>
                      <strong>Interactive Supply Chain Mapping:</strong>{' '}
                      Visualize your entire network to identify and mitigate
                      risks before they impact your business.
                    </li>
                  </ul>
                  <div className="fw-bold my-2 d-md-none d-block">
                    <p>
                      Verified Visibility with SupplyPassport compliance profile
                    </p>
                    <a
                      href="https://supplypassport.app/supplier/dc632034-a493-4b86-9386-4c2c62e70134"
                      target="_blank"
                      rel="noopener noreferrer"
                      umami-track="Register landing profile click"
                      className="btn btn-outline-light"
                    >
                      Example Profile
                    </a>
                  </div>
                </div>
                <div className="mt-0">
                  <div className="text-white mb-3">
                    <strong>Wear Your Compliance with Pride</strong>
                    <p className="my-1">
                      Example compliance badges you can share with clients after
                      completing assessments.
                    </p>
                  </div>
                  <div className="d-flex flex-row flex-wrap gap-3 align-items-start justify-content-around">
                    <a
                      href="https://supplypassport.app/public/report/f12a38eb-5dc1-4255-a62b-0f907d40f526"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleLandingBadgeClick('ESRS Compliance Score')
                      }
                    >
                      <img
                        src="https://qxeanjrlvevtuyyfjebf.supabase.co/functions/v1/get-supplier-report-badge?id=e4e41b22-1514-4a5b-ad47-760c01e057d6&style=badge6"
                        alt="ESRS Compliance Score"
                        className="register-badge"
                      />
                    </a>

                    <a
                      href="https://supplypassport.app/public/report/d816c1d0-325c-479d-94c1-08d6d7cf34a4"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleLandingBadgeClick('ESG Compliance Score')
                      }
                    >
                      <img
                        src="https://qxeanjrlvevtuyyfjebf.supabase.co/functions/v1/get-supplier-report-badge?id=2d927ebf-006d-4d35-8c54-6f83d96687de&style=badge7"
                        alt=""
                        className="register-badge"
                      />
                    </a>
                    <a
                      href="https://supplypassport.app/public/report/7bde179c-55e9-40b8-baa5-fed13c905580"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleLandingBadgeClick('PPWR Compliance Score')
                      }
                    >
                      <img
                        src="https://qxeanjrlvevtuyyfjebf.supabase.co/functions/v1/get-supplier-report-badge?id=0d7e656f-fb26-4710-9bc2-835a1e1b4c19&style=badge1"
                        alt="PPWR Compliance Score"
                        className="register-badge"
                      />
                    </a>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex flex-column justify-content-end align-items-stretch align-items-md-end h-100 text-center p-4">
                  <Button
                    size="lg"
                    onClick={handleLandingPlansClick}
                    variant="light"
                    className="w-100"
                  >
                    View Plans & Get Started
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        ) : !selectedPlanId ? (
          <div className="text-center mb-4">
            <img
              src={logoDark}
              alt="SupplyPassport Logo"
              height="60"
              className="mb-3"
            />
            <h1 className="fw-bold">Choose Your Plan</h1>
            <p className="text-muted mb-2">
              Select the plan that best fits your needs to get started with
              SupplyPassport.
            </p>
            {loadingPlans ? (
              <div className="text-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <PlanSelect
                onSelect={(planId, billingInterval) => {
                  setSelectedPlanId(planId);
                  setSelectedBillingInterval(billingInterval);
                  setRegisterStep('form');
                }}
                plans={plans}
              />
            )}
          </div>
        ) : (
          <Card className="shadow-lg rounded-3 overflow-hidden">
            <Row className="g-0">
              {/* Left column */}
              <Col
                md={6}
                className="bg-primary text-white p-4 d-flex flex-column justify-content-center align-items-center text-center"
              >
                <div>
                  <img
                    src={logo}
                    alt="SupplyPassport Logo"
                    className="mb-4 callout-logo"
                  />
                  <h2 className="fw-bold">Join SupplyPassport</h2>
                  <p className="mt-3 mb-0">
                    Simplify your supplier compliance process and manage risks
                    with ease.
                  </p>
                </div>
              </Col>

              {/* Right column */}
              <Col
                md={6}
                className="bg-white p-4"
                style={{ minHeight: '500px' }}
              >
                <div className="mb-4">
                  <h2 className="h4 fw-bold mb-1">Create an account</h2>
                  <p className="text-muted mb-0">
                    Start managing your compliance today
                  </p>
                  {!success && (
                    <p>
                      Selected plan: <strong>{selectedPlanName}</strong>{' '}
                      <span className="text-muted">
                        ({selectedBillingInterval})
                      </span>{' '}
                      <Button
                        variant="link"
                        size="sm"
                        onClick={handleChangePlan}
                      >
                        Change
                      </Button>
                    </p>
                  )}
                </div>

                {error && <Alert variant="danger">{error}</Alert>}
                {success ? (
                  <Alert variant="success">{success}</Alert>
                ) : (
                  <Form onSubmit={handleRegister} className="mb-3">
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="formFirstName">
                          <Form.Label className="small">First name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="John"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="formLastName">
                          <Form.Label className="small">Last name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Doe"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="formEmail">
                      <Form.Label className="small">Email address</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-envelope" />
                        </span>
                        <Form.Control
                          type="email"
                          placeholder="you@company.com"
                          className="border-start-0"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={loading}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formPassword">
                      <Form.Label className="small">Password</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-lock" />
                        </span>
                        <Form.Control
                          type="password"
                          placeholder="••••••••"
                          className="border-start-0"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          minLength={8}
                          required
                          disabled={loading}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group
                      className="mb-3"
                      controlId="formConfirmPassword"
                    >
                      <Form.Label className="small">
                        Confirm password
                      </Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-lock" />
                        </span>
                        <Form.Control
                          type="password"
                          placeholder="••••••••"
                          className="border-start-0"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          disabled={loading}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formTermsPrivacy">
                      <Form.Check
                        type="checkbox"
                        checked={acceptedTermsAndPrivacy}
                        onChange={(e) =>
                          setAcceptedTermsAndPrivacy(e.target.checked)
                        }
                        disabled={loading}
                        label={
                          <>
                            I agree to the{' '}
                            <a
                              href="/terms-or-service"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Terms of Service
                            </a>{' '}
                            and{' '}
                            <a
                              href="/privacy-policy"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Privacy Policy
                            </a>
                            .
                          </>
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formDpa">
                      <Form.Check
                        type="checkbox"
                        checked={acceptedDpa}
                        onChange={(e) => setAcceptedDpa(e.target.checked)}
                        disabled={loading}
                        label={
                          <>
                            I agree to the{' '}
                            <a
                              href="/data-processing-agreement"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Data Processing Agreement
                            </a>
                            .
                          </>
                        }
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      className="w-100 btn-primary my-4"
                      disabled={
                        loading || !acceptedTermsAndPrivacy || !acceptedDpa
                      }
                    >
                      {loading ? 'Registering...' : 'Register'}
                    </Button>
                  </Form>
                )}
              </Col>
            </Row>
          </Card>
        )}

        <p className="text-center small mt-4 mb-0">
          Already have an account?{' '}
          <Link to="/login" className="fw-semibold">
            Login here
          </Link>
        </p>

        <div className="text-center mt-3 small text-muted">
          By signing up, you agree to our{' '}
          <Link to="/terms-or-service" className="text-decoration-none">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy-policy" className="text-decoration-none">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link
            to="/data-processing-agreement"
            className="text-decoration-none"
          >
            Data Processing Agreement
          </Link>
          .
        </div>
      </Container>
    </div>
  );
}
